/**
 * Keeps available products ahead of sold-out products in collection and
 * search listings without changing Shopify's sort order or pagination.
 *
 * Sold-out cards stay in the native grid (so the pagination component can
 * still calculate the next page), but are hidden until the last native page
 * has been rendered. At that point the buffer is released in native order.
 */

const RESULT_LIST_SELECTOR = 'results-list[infinite-scroll="true"]';
const CARD_SELECTOR = '[ref="cards[]"]';

class StockOrderList {
  #resultList;
  #observer;
  #scheduled = false;
  #processing = false;

  constructor(resultList) {
    this.#resultList = resultList;
  }

  connect() {
    this.#observer = new MutationObserver(() => this.#schedule());
    this.#observer.observe(this.#resultList, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-last-page', 'data-page', 'data-stock-available'],
    });

    this.#schedule();
  }

  disconnect() {
    this.#observer?.disconnect();
  }

  #schedule() {
    if (this.#scheduled) return;

    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      this.#process();
    });
  }

  #process() {
    if (this.#processing || !this.#resultList.isConnected) return;

    const grid = this.#resultList.querySelector('[ref="grid"]');
    if (!grid) return;

    const cards = [...grid.querySelectorAll(`:scope > ${CARD_SELECTOR}`)];
    if (!cards.length) return;

    this.#processing = true;
    try {
      const available = cards.filter((card) => card.dataset.stockAvailable === 'true');
      const soldOut = cards.filter((card) => card.dataset.stockAvailable !== 'true');
      const lastPage = Number(grid.dataset.lastPage || 0);
      const loadedLastPage = cards.some((card) => Number(card.dataset.page) >= lastPage);
      const complete = lastPage > 0 && loadedLastPage;

      // Before the final page, preserve page groups so the native paginator
      // can continue to identify the highest loaded page from the last card.
      // Once pagination is complete, all available cards can safely move
      // ahead of the released sold-out buffer.
      const orderedCards = complete ? [...available, ...soldOut] : this.#orderByPage(cards);
      const orderChanged = orderedCards.some((card, index) => card !== cards[index]);
      if (orderChanged) grid.append(...orderedCards);

      for (const card of soldOut) {
        card.hidden = !complete;
        card.dataset.stockBuffered = complete ? 'false' : 'true';
      }

      for (const card of available) {
        card.hidden = false;
        card.dataset.stockBuffered = 'false';
      }

      this.#resultList.dataset.stockOrderComplete = complete ? 'true' : 'false';

      // The native observer has no next page after this point. Removing its
      // sentinel also prevents a stale intersection event after the final
      // reorder from requesting the last page a second time.
      if (complete) {
        this.#resultList.querySelector('[ref="viewMoreNext"]')?.remove();
      }
    } finally {
      this.#processing = false;
    }
  }

  #orderByPage(cards) {
    const groups = [];
    const groupsByPage = new Map();

    for (const card of cards) {
      const page = card.dataset.page || '0';
      let group = groupsByPage.get(page);
      if (!group) {
        group = { available: [], soldOut: [] };
        groupsByPage.set(page, group);
        groups.push(group);
      }

      if (card.dataset.stockAvailable === 'true') {
        group.available.push(card);
      } else {
        group.soldOut.push(card);
      }
    }

    return groups.flatMap(({ available: pageAvailable, soldOut: pageSoldOut }) => [
      ...pageAvailable,
      ...pageSoldOut,
    ]);
  }
}

const controllers = new WeakMap();

function connectResultList(resultList) {
  if (controllers.has(resultList)) return;

  const controller = new StockOrderList(resultList);
  controllers.set(resultList, controller);
  controller.connect();
}

function scanResultLists(root = document) {
  if (root instanceof Element && root.matches(RESULT_LIST_SELECTOR)) {
    connectResultList(root);
  }

  root.querySelectorAll?.(RESULT_LIST_SELECTOR).forEach(connectResultList);
}

function start() {
  scanResultLists();

  const pageObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) scanResultLists(node);
      }
    }
  });

  pageObserver.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
